import { ImagesSubComponent } from '../images.sub.component';
import { getCurrentMajorBreakpoint, MajorBreakpoint } from '../../../_common/client/breakpoint';
import { dom } from '@otto-ec/global-resources/dom';
import { FALLBACK_IMAGE_ID, getImageUrl } from '../../../_common/client/images';

export class AlternateImagesSubComponent extends ImagesSubComponent {
    public rootSelector = '.js_pdp_alternate-images';

    private viewportHeight = 0;
    private viewportTop = 0;
    private viewportBottom = 0;
    private thumbnailListTop = 0;
    private thumbnailListHeight = 0;

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }

        if (getCurrentMajorBreakpoint() === MajorBreakpoint.Desktop) {
            await this.loadImages();
            this.moveThumbnailSelection(0);
            this.showOrHideButtons();
            this.setEventEnterHandlers();
        }
    }

    protected onImageChanged(index: number): void {
        if (!this.isPresent()) {
            return;
        }

        this.goToThumbnail(index);
    }

    private async loadImages(): Promise<void> {
        const thumbnailElements = this.getThumbnailElements();
        await Promise.all(
            thumbnailElements.map((thumbnailElement, idx) => {
                return new Promise<void>(resolve => {
                    const imageId = thumbnailElement.dataset.imageId || FALLBACK_IMAGE_ID;
                    const imgElement = document.createElement('img');
                    imgElement.onload = () => resolve();
                    imgElement.onerror = () => {
                        imgElement.src = imgElement.src.replace(imageId, FALLBACK_IMAGE_ID);
                        imgElement.onerror = () => {
                            resolve();
                        };
                    };
                    imgElement.src = getImageUrl(imageId, '$001PICT36$&fmt=auto');
                    imgElement.alt = `Detailansicht des Produkts ${idx}`;
                    thumbnailElement.appendChild(imgElement);
                });
            })
        );
    }

    private getViewportElement(): HTMLButtonElement {
        return this.getChildElement('.js_pdp_alternate-images__viewport');
    }

    private getThumbnailListElement(): HTMLButtonElement {
        return this.getChildElement('.js_pdp_alternate-images__thumbnail-list');
    }

    private getThumbnailElements(): HTMLButtonElement[] {
        return this.getChildElements('.js_pdp_alternate-images__thumbnail');
    }

    private getBtnUpElement(): HTMLButtonElement {
        return this.getChildElement('.js_pdp_alternate-images__btn--up');
    }

    private getBtnDownElement(): HTMLButtonElement {
        return this.getChildElement('.js_pdp_alternate-images__btn--down');
    }

    private moveThumbnailSelection(newIndex: number): void {
        this.getThumbnailElements().forEach((thumbnail, index) => {
            if (index === newIndex) {
                thumbnail.classList.add('pdp_alternate-images__thumbnail--selected');
            } else {
                thumbnail.classList.remove('pdp_alternate-images__thumbnail--selected');
            }
        });
    }

    private moveThumbnailListTo(newAbsoluteViewportTop: number): void {
        let newRelativeViewportTop = newAbsoluteViewportTop - this.thumbnailListTop;
        if (newRelativeViewportTop <= 0) {
            newRelativeViewportTop = 0;
            this.getBtnUpElement().setAttribute('disabled', '');
            this.getBtnDownElement().removeAttribute('disabled');
        } else if (newRelativeViewportTop > this.thumbnailListHeight - this.viewportHeight) {
            newRelativeViewportTop = this.thumbnailListHeight - this.viewportHeight;
            this.getBtnUpElement().disabled = false;
            this.getBtnDownElement().setAttribute('disabled', '');
        } else {
            this.getBtnUpElement().removeAttribute('disabled');
            this.getBtnDownElement().removeAttribute('disabled');
        }

        const newThumbnailListTop = 0 - newRelativeViewportTop;

        this.getThumbnailListElement().style.top = newThumbnailListTop + 'px';
    }

    private static getBoundingClientRect(el: HTMLElement): DOMRect {
        if (el && typeof el.getBoundingClientRect === 'function') {
            return el.getBoundingClientRect();
        } else {
            return new DOMRect(0, 0, 0, 0);
        }
    }

    private refreshSavedElementLocations(): void {
        const viewportBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(this.getViewportElement());
        const thumbnailListBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(this.getThumbnailListElement());

        // Use bottom and top values, because Internet Explorer rounds the height of BoundingClientRect
        this.viewportHeight = viewportBoundingClientRect.bottom - viewportBoundingClientRect.top;
        this.viewportTop = viewportBoundingClientRect.top;
        this.viewportBottom = viewportBoundingClientRect.bottom;
        this.thumbnailListTop = thumbnailListBoundingClientRect.top;
        this.thumbnailListHeight = thumbnailListBoundingClientRect.bottom - thumbnailListBoundingClientRect.top;
    }

    private goToThumbnail(index: number): void {
        const thumbnailElements = this.getThumbnailElements();
        if (thumbnailElements.length > 1) {
            if (!thumbnailElements[index]) {
                throw 'going to thumbnail of index: ' + index + ' is not possible. ThumbnailElements length is ' + thumbnailElements.length;
            }

            this.refreshSavedElementLocations();

            const thumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(thumbnailElements[index]);
            this.moveThumbnailListTo(thumbnailBoundingClientRect.top);

            this.moveThumbnailSelection(index);
        }
    }

    private showOrHideButtons(): void {
        this.refreshSavedElementLocations();

        if (Math.floor(this.thumbnailListHeight) > Math.floor(this.viewportHeight)) {
            this.getBtnUpElement().classList.remove('oc-visually-hidden');
            this.getBtnDownElement().classList.remove('oc-visually-hidden');
        }
    }

    private trackButtonClick(direction: string): void {
        this.trackEvent({
            product_DifferentImageScroll: direction,
            product_DifferentImageMethod: 'button',
        });
    }

    // Ermittelt den untersten, sich nicht mehr vollständig im Viewport befindenden Thumbnail. Die Oberkante des
    // Thumbnails ist dann die zukünftige absolute obere Position der Thumbnail-Liste zum Viewport.
    private getNewAbsoluteViewportTopMoveDown(): number {
        const thumbnailElements = this.getThumbnailElements();
        for (let i = 0; i < thumbnailElements.length; i++) {
            const thumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(thumbnailElements[i]);
            if (thumbnailBoundingClientRect.bottom > this.viewportBottom) {
                return thumbnailBoundingClientRect.top;
            }
        }
        return 0;
    }

    // Ermittelt den obersten, sich vollständig im Viewport befindenden Thumbnail und berechnet dann aus der Unterkante
    // des vorigen Thumbnails und der Höhe des Viewports die neue absolute obere Position der Thumbnail-Liste zum
    // Viewport.
    private getNewAbsoluteViewportTopMoveUp(): number {
        const itemElements = this.getThumbnailElements();
        if (itemElements.length > 1) {
            let previousThumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(itemElements[0]);
            for (let i = 1; i < itemElements.length; i++) {
                const thumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(itemElements[i]);
                if (thumbnailBoundingClientRect.top >= this.viewportTop) {
                    return previousThumbnailBoundingClientRect.bottom - this.viewportHeight;
                }
                previousThumbnailBoundingClientRect = thumbnailBoundingClientRect;
            }
        }
        return 0;
    }

    private handleMouseEnterEvent(event: MouseEvent): void {
        const { target } = event;
        const thumbnailElement = dom.getParentByClassName<HTMLElement>(target as Element, 'js_pdp_alternate-images__thumbnail');
        const index = parseInt(thumbnailElement?.dataset.index || '0', 10);

        if (!thumbnailElement?.classList.contains('pdp_alternate-images__thumbnail--selected')) {
            this.moveThumbnailSelection(index);
            this.changeImage(index);
        }
    }

    private handleUpButtonClickEvent(): void {
        this.refreshSavedElementLocations();
        this.moveThumbnailListTo(this.getNewAbsoluteViewportTopMoveUp());
        this.trackButtonClick('up');
    }

    private handleDownButtonClickEvent(): void {
        this.refreshSavedElementLocations();
        this.moveThumbnailListTo(this.getNewAbsoluteViewportTopMoveDown());
        this.trackButtonClick('down');
    }

    private setEventEnterHandlers(): void {
        const listElement = this.getThumbnailListElement();
        o_util.event.delegate(listElement, 'click', '.js_pdp_alternate-images__thumbnail', (event: MouseEvent) =>
            this.handleMouseEnterEvent(event)
        );
        o_util.event.delegate(
            listElement,
            'mouseenter',
            '.js_pdp_alternate-images__thumbnail',
            (event: MouseEvent) => this.handleMouseEnterEvent(event),
            true
        );
        this.getBtnUpElement().addEventListener('click', () => this.handleUpButtonClickEvent());
        this.getBtnDownElement().addEventListener('click', () => this.handleDownButtonClickEvent());
    }
}
